import React from 'react';
import { Link } from 'react-router-dom';
import { Target, TrendingUp, DollarSign, Lock } from 'lucide-react';
import { ProductTargetPanel } from '@/components/ProductTargetPanel';
import { TargetCelebration } from '@/components/TargetCelebration';

/**
 * TargetAchievementCard - Reusable component for displaying target achievement data
 * @param {Object} targetAchievement - Target achievement data object
 * @param {boolean} showDetailLink - Whether to show the "Detaylı Görüntüle" link (default: true)
 * @param {string} className - Additional CSS classes for the card container
 */
const TargetAchievementCard = ({ targetAchievement, showDetailLink = true, className = '' }) => {
  if (!targetAchievement || !targetAchievement.has_target) {
    return (
      <div className={`bg-gray-100 rounded-xl shadow-lg p-6 text-center h-full flex flex-col items-center justify-center ${className}`}>
        <Target className="w-12 h-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Hedef Tanımlanmamış</h3>
        <p className="text-sm text-gray-500 mb-4">
          Bu ay için henüz hedef belirlenmemiş.
        </p>
        {showDetailLink && (
          <Link 
            to="/target-achievement" 
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Hedef Gerçekleştirme Sayfası →
          </Link>
        )}
      </div>
    );
  }

  const { achievement_rate, target_points, achieved_points, commission } = targetAchievement;
  const barem = targetAchievement.applied_barem ?? (achievement_rate >= 180 ? 3 : achievement_rate >= 120 ? 2 : achievement_rate >= 100 ? 1 : 0);

  return (
    <div className={`bg-white rounded-xl shadow-xl p-4 sm:p-6 border-2 border-gray-200 h-full ${className}`} data-testid="dashboard-target-card">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-lg">
          <Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <h3 className="text-base sm:text-lg font-bold text-gray-800">Hedef Gerçekleştirme</h3>
        {targetAchievement.is_closed && (
          <span className="ml-auto flex items-center gap-1 text-xs font-semibold bg-gray-800 text-white px-2 py-0.5 rounded-full" data-testid="dashboard-month-closed-badge">
            <Lock className="w-3 h-3" />Ay Kapatıldı
          </span>
        )}
      </div>
      <TargetCelebration a={targetAchievement} />
      
      {/* Progress Bar */}
      <div className="mb-3 sm:mb-4">
        <div className="flex items-center justify-between text-xs sm:text-sm mb-2 text-gray-700">
          <span>Gerçekleşme Oranı</span>
          <span className="font-bold text-lg sm:text-xl text-blue-600">{achievement_rate.toFixed(1)}%</span>
        </div>
        <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(achievement_rate, 200)}%` }}
          />
        </div>
      </div>
      
      {/* Hedef vs Gerçekleşen */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="bg-blue-50 border border-blue-200 p-2 sm:p-3 rounded-lg">
          <div className="flex items-center gap-1 mb-1">
            <Target className="w-3 h-3 text-blue-600" />
            <span className="text-[10px] sm:text-xs text-blue-600 font-semibold">HEDEF</span>
          </div>
          <p className="text-base sm:text-xl font-bold text-gray-800">{target_points.toLocaleString('tr-TR')}</p>
          <p className="text-[10px] sm:text-xs text-gray-600">Puan</p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 p-2 sm:p-3 rounded-lg">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-3 h-3 text-purple-600" />
            <span className="text-[10px] sm:text-xs text-purple-600 font-semibold">GERÇEKLEŞEN</span>
          </div>
          <p className="text-base sm:text-xl font-bold text-gray-800">{achieved_points.toLocaleString('tr-TR')}</p>
          <p className="text-[10px] sm:text-xs text-gray-600">Puan</p>
        </div>
      </div>
      
      {/* Hakediş */}
      <div className="bg-yellow-400 text-gray-900 p-4 rounded-lg mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            <span className="text-sm font-medium">Hakediş</span>
          </div>
          {barem === 3 ? (
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-bold">
              EXTRA BONUSLU PRİM
            </span>
          ) : barem === 2 ? (
            <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
              BONUSLU PRİM
            </span>
          ) : null}
        </div>
        <p className="text-2xl font-bold mt-1" data-testid="dashboard-target-commission">
          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(commission)}
        </p>
      </div>

      <div className="mb-3"><ProductTargetPanel a={targetAchievement} /></div>
      
      {/* Teşvik Mesajı */}
      {targetAchievement.product_target_rule && achievement_rate >= 120 && !(targetAchievement.camera_ok && targetAchievement.device_ok) ? (
        <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg text-center" data-testid="dashboard-target-capped-msg">
          <p className="text-sm font-bold">Kamera/Cihaz hedefi tamamlanmadığı için prim 1. baremle sınırlı</p>
          <p className="text-xs mt-1 opacity-90">Üst barem primi için eksik kamera/cihaz satışlarını tamamlayın</p>
        </div>
      ) : achievement_rate >= 180 ? (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-3 rounded-lg text-center">
          <p className="text-sm font-bold">✓ Üstün Performans - Extra Bonuslu Prim Hak Edildi</p>
        </div>
      ) : achievement_rate >= 120 ? (
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-3 rounded-lg text-center">
          <p className="text-xs font-semibold mb-1">📊 Hedef İlerleme</p>
          <p className="text-sm font-bold">%180 hedefine {((target_points * 1.8) - achieved_points).toFixed(0)} puan kaldı</p>
          <p className="text-xs mt-1 opacity-90">
            Extra Bonuslu Prim için devam edin
          </p>
        </div>
      ) : achievement_rate >= 100 ? (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-3 rounded-lg text-center">
          <p className="text-xs font-semibold mb-1">📊 Hedef İlerleme</p>
          <p className="text-sm font-bold">%120 hedefine {((target_points * 1.2) - achieved_points).toFixed(0)} puan kaldı</p>
          <p className="text-xs mt-1 opacity-90">
            Bonuslu Prim için devam edin
          </p>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-3 rounded-lg text-center">
          <p className="text-xs font-semibold mb-1">📊 Hedef Durumu</p>
          <p className="text-sm font-bold">%100 hedefe {(target_points - achieved_points).toFixed(0)} puan kaldı</p>
          <p className="text-xs mt-1 opacity-90">
            Hedef gerçekleştirme için ilerlemeye devam edin
          </p>
        </div>
      )}
      
      <div className="mt-3 bg-gray-50 p-3 rounded-lg text-xs text-gray-600" data-testid="dashboard-target-calc-info">
        <p className="font-semibold mb-1">Hakediş Hesaplama:</p>
        {barem === 3 ? <p>%180+ → Puan × 0.25 + 7.500 TL + EXTRA BONUSLU PRİM</p>
          : barem === 2 ? <p>%120+ → Puan × 0.25 + 2.500 TL + BONUSLU PRİM</p>
          : barem === 1 ? <p>%100+ → Puan × 0.15</p>
          : <p>%100 altı → Hakediş yok</p>}
      </div>
      {showDetailLink && (
        <Link 
          to="/target-achievement" 
          className="block mt-4 text-center text-sm bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-all font-medium"
        >
          Detaylı Görüntüle →
        </Link>
      )}
    </div>
  );
};

export default TargetAchievementCard;
